import { AxiosResponse } from 'axios';
import { TAuthResponse } from 'response/AuthResponse';
import { TAvatar, TFio, TPhone } from 'response/UserResponse';
import { TCityResponse, TCityItemsResponseRts, TCityItemsResponseRtc, TFeatureCollection } from 'response/CityResponse';
import { TSetProgram } from 'response/CrossroadResponse';
import {
  TImgResponseRTc,
  TTelemetryResponseRTc,
  TStreetsResponseRTc,
  TSegmentsLastHourResponseRTc,
  TEventsResponseRTc,
  TKtsHoursResponseRTc,
} from 'response/RTcResponce';
import {
  TTelemetryResponseRTs,
  TImgResponseRTs,
  TStreetsResponseRTs,
  TSegmentsLastHourResponseRTs,
  TEventsResponseRTs,
  TPhasesResponseRTs,
  TKtsHoursResponseRTs,
  TSoHoursResponseRTs,
} from 'response/RTsResponce';
import {
  TGetDevicersCross,
  TGetCitySegmentsCross,
  TGetCityStreetsCross,
  TGetCityAnalyticsCross,
  TGetCityCross,
} from 'response/CrossResponse';
import { TArchiveResponse, TDescript } from 'response/ArchiveResponse';
import { TUdsResponse, TUdss } from 'response/UdsResponse';
import { TCommentResponse, TComments } from 'response/CommentResponse';
import { instancePrivate, instancePublic } from './instances';

// API для аутентификации пользователя
export const authAPI = {
  async login(username: string, password: string): Promise<AxiosResponse<TAuthResponse>> {
    return instancePublic.post<TAuthResponse>('/auth/login', {
      email: username,
      password,
    });
  },
  async checkAuth(): Promise<AxiosResponse<TAuthResponse>> {
    return instancePrivate.get<TAuthResponse>('/auth/check', {});
  },
};

// API для получения и смены автарки пользователя
export const userAPI = {
  async avatar(): Promise<AxiosResponse<TAvatar>> {
    return instancePrivate.get<TAvatar>('/user/get_avatar', {});
  },
  async fio(fio: string): Promise<AxiosResponse<TFio>> {
    return instancePrivate.post<TFio>('/user/edit_fio', {
      fio,
    });
  },
  async phone(phone: string): Promise<AxiosResponse<TPhone>> {
    return instancePrivate.post<TPhone>('/user/edit_phone', {
      phone,
    });
  },
  async edit_avatar(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('/user/edit_avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// API для получения данных Города
export const cityAPI = {
  async detail(): Promise<AxiosResponse<TCityResponse>> {
    return instancePrivate.get<TCityResponse>('/city/detail', {});
  },
  async itemsTelemetryRts(): Promise<AxiosResponse<TCityItemsResponseRts>> {
    return instancePrivate.get<TCityItemsResponseRts>('/city/items_telemetry_rts', {});
  },
  async itemsTelemetryRtc(): Promise<AxiosResponse<TCityItemsResponseRtc>> {
    return instancePrivate.get<TCityItemsResponseRtc>('/city/items_telemetry_rtc', {});
  },
  async transport(): Promise<AxiosResponse<TFeatureCollection>> {
    return instancePrivate.get<TFeatureCollection>('/city/transport_situation', {});
  },
};

// API для получения данных конкретного перекрестка
export const crossroadAPI = {
  setProgramRts(id: string, program: string): Promise<AxiosResponse<TSetProgram>> {
    return instancePrivate.post<TSetProgram>(`/crossroad/rts/${id}/set_program`, {
      oID: id,
      program,
    });
  },
  setCustomProgramRts(id: string, sequence: string[], durations: string[]): Promise<AxiosResponse<TSetProgram>> {
    return instancePrivate.post<TSetProgram>(`/crossroad/rts/${id}/set_custom_program`, {
      oID: id,
      sequence,
      durations,
    });
  },
  setCustomUFProgramRts(id: string, phase: number): Promise<AxiosResponse<TSetProgram>> {
    return instancePrivate.post<TSetProgram>(`/crossroad/rts/${id}/set_uf_program`, {
      oID: id,
      phase,
    });
  },
  async day_data(id: string, utime: number): Promise<AxiosResponse<Blob>> {
    return instancePrivate.post<Blob>(
      `/crossroad/rts/${id}/day_reports`,
      {
        utime,
      },
      {
        responseType: 'blob',
      },
    );
  },
  async data_reports(id: string, utime_start: number, utime_end: number): Promise<AxiosResponse<Blob>> {
    return instancePrivate.post<Blob>(
      `/crossroad/rts/${id}/data_reports`,
      {
        utime_start,
        utime_end,
      },
      {
        responseType: 'blob',
      },
    );
  },
  async all_reports(id: string, utime_start: number, utime_end: number): Promise<AxiosResponse<Blob>> {
    return instancePrivate.post<Blob>(
      `/crossroad/rts/${id}/all_reports`,
      {
        utime_start,
        utime_end,
      },
      {
        responseType: 'blob',
      },
    );
  },
  async difference_reports(
    id: string,
    utime_start: number,
    utime_end: number,
    utime_start_two: number,
    utime_end_two: number,
  ): Promise<AxiosResponse<Blob>> {
    return instancePrivate.post<Blob>(
      `/crossroad/rts/${id}/difference_reports`,
      {
        utime_start,
        utime_end,
        utime_start_two,
        utime_end_two,
      },
      {
        responseType: 'blob',
      },
    );
  },
};

// API для получения данных нового перекрестка
export const crossAPI = {
  getDevicesCross(id: string): Promise<AxiosResponse<TGetDevicersCross>> {
    return instancePrivate.post<TGetDevicersCross>(`/cross/${id}/get_devices`, {});
  },
  getCitySegmentsCross(): Promise<AxiosResponse<TGetCitySegmentsCross>> {
    return instancePrivate.get<TGetCitySegmentsCross>('/cross/get_city_segments', {});
  },
  getCityCross(): Promise<AxiosResponse<TGetCityCross>> {
    return instancePrivate.get<TGetCityCross>('/cross/get_city_cross', {});
  },
  getCityStreetsCross(): Promise<AxiosResponse<TGetCityStreetsCross>> {
    return instancePrivate.get<TGetCityStreetsCross>('/cross/get_city_streets', {});
  },
  getCityAnalyticsCross(
    oIDs: string | string[],
    objectDirection: string,
    utime: number,
  ): Promise<AxiosResponse<TGetCityAnalyticsCross>> {
    return instancePrivate.post<TGetCityAnalyticsCross>('/cross/get_city_analytics', {
      oIDs,
      objectDirection,
      utime,
    });
  },
};

// API для получения данных конкретной RTs
export const rtsAPI = {
  telemetryRTs(id: string): Promise<AxiosResponse<TTelemetryResponseRTs>> {
    return instancePrivate.get<TTelemetryResponseRTs>(`/rts/${id}/telemetry`, {});
  },
  imgRTs(id: string): Promise<AxiosResponse<TImgResponseRTs>> {
    return instancePrivate.get<TImgResponseRTs>(`/rts/${id}/img`, {});
  },
  stopVideoRTs(id: string, idVprocessor: string): Promise<AxiosResponse<string>> {
    return instancePrivate.post<string>(`/rts/${id}/${idVprocessor}/stop_video`, {});
  },
  streetsRTs(id: string): Promise<AxiosResponse<TStreetsResponseRTs>> {
    return instancePrivate.get<TStreetsResponseRTs>(`/rtc/${id}/streets`, {});
  },
  ktsLastHourRTs(id: string): Promise<AxiosResponse<TSegmentsLastHourResponseRTs>> {
    return instancePrivate.post<TSegmentsLastHourResponseRTs>(`/rts/${id}/kts_last_hour`, {});
  },
  eventsLastRTs(id: string): Promise<AxiosResponse<TEventsResponseRTs>> {
    return instancePrivate.post<TEventsResponseRTs>(`/rts/${id}/events_last`, {});
  },
  eventsAllsRTs(id: string, utime_today: number): Promise<AxiosResponse<TEventsResponseRTs>> {
    return instancePrivate.post<TEventsResponseRTs>(`/rts/${id}/events_alls`, {
      utime_today,
    });
  },
  rtsPhasesDetail(id: string): Promise<AxiosResponse<TPhasesResponseRTs>> {
    return instancePrivate.get<TPhasesResponseRTs>(`/rts/${id}/phases`, {});
  },
  ktsHoursRTs(id: string): Promise<AxiosResponse<TKtsHoursResponseRTs>> {
    return instancePrivate.post<TKtsHoursResponseRTs>(`/rts/${id}/kts_hours`, {});
  },
  soHoursRTs(id: string): Promise<AxiosResponse<TSoHoursResponseRTs>> {
    return instancePrivate.post<TSoHoursResponseRTs>(`/rts/${id}/so_hours`, {});
  },
};

// API для получения данных конкретной RTc
export const rtcAPI = {
  telemetryRTc(id: string): Promise<AxiosResponse<TTelemetryResponseRTc>> {
    return instancePrivate.get<TTelemetryResponseRTc>(`/rtc/${id}/telemetry`, {});
  },
  imgRTc(id: string): Promise<AxiosResponse<TImgResponseRTc>> {
    return instancePrivate.get<TImgResponseRTc>(`/rtc/${id}/img`, {});
  },
  stopVideoRTc(id: string, idVprocessor: string): Promise<AxiosResponse<string>> {
    return instancePrivate.post<string>(`/rtc/${id}/${idVprocessor}/stop_video`, {});
  },
  streetsRTc(id: string): Promise<AxiosResponse<TStreetsResponseRTc>> {
    return instancePrivate.get<TStreetsResponseRTc>(`/rtc/${id}/streets`, {});
  },
  ktsLastHourRTc(id: string): Promise<AxiosResponse<TSegmentsLastHourResponseRTc>> {
    return instancePrivate.post<TSegmentsLastHourResponseRTc>(`/rtc/${id}/kts_last_hour`, {});
  },
  eventsLastRTc(id: string): Promise<AxiosResponse<TEventsResponseRTc>> {
    return instancePrivate.post<TEventsResponseRTc>(`/rtc/${id}/events_last`, {});
  },
  eventsAllsRTc(id: string, utime_today: number): Promise<AxiosResponse<TEventsResponseRTc>> {
    return instancePrivate.post<TEventsResponseRTc>(`/rtc/${id}/events_alls`, {
      utime_today,
    });
  },
  ktsHoursRTc(id: string): Promise<AxiosResponse<TKtsHoursResponseRTc>> {
    return instancePrivate.post<TKtsHoursResponseRTc>(`/rtc/${id}/kts_hours`, {});
  },
};

//  API для проверки соединения с сервером
export const utilAPI = {
  async ping(): Promise<AxiosResponse<null>> {
    return instancePublic.get<null>('/utils/ping', {});
  },
};

//  API для получения данных Архива
export const getTableAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/podd', {});
  },
};

export const getReportsAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/reports', {});
  },
};

export const getProjectsAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/projects', {});
  },
};

export const getSchemesAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/schemes', {});
  },
};

export const getLettersAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/letters', {});
  },
};

export const getTechsAPI = {
  async detail(): Promise<AxiosResponse<TArchiveResponse>> {
    return instancePrivate.get<TArchiveResponse>('/archive/techs', {});
  },
};

export const postTableAPI = {
  async post_data(
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.post<TDescript>('/archive/podd', {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const postReportsAPI = {
  async post_data(
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.post<TDescript>('/archive/reports', {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const postProjectsAPI = {
  async post_data(
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.post<TDescript>('/archive/projects', {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const postLettersAPI = {
  async post_data(
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.post<TDescript>('/archive/letters', {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const postTechsAPI = {
  async post_data(
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.post<TDescript>('/archive/techs', {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const putCommentTableAPI = {
  async put_comment(
    id: number,
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.put<TDescript>(`/archive/podd_comment/${id}`, {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const putCommentReportsAPI = {
  async put_comment(
    id: number,
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.put<TDescript>(`/archive/reports_comment/${id}`, {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const putCommentProjectsAPI = {
  async put_comment(
    id: number,
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.put<TDescript>(`/archive/projects_comment/${id}`, {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const putCommentLettersAPI = {
  async put_comment(
    id: number,
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.put<TDescript>(`/archive/letters_comment/${id}`, {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const putCommentTechsAPI = {
  async put_comment(
    id: number,
    name_object: string,
    target_object: string,
    file_description: string,
    file_name: string,
    time: string,
    comment: string,
  ): Promise<AxiosResponse<TDescript>> {
    return instancePrivate.put<TDescript>(`/archive/techs_comment/${id}`, {
      name_object,
      target_object,
      file_description,
      file_name,
      time,
      comment,
    });
  },
};

export const fileAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/file_podd/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const fileReportsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/file_reports/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const fileProjectsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/file_projects/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const fileLettersAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/file_letters/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const fileTechsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/file_techs/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const updateAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/update_file/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const updateReportsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/update_file_reports/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const updateProjectsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/update_file_projects/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const updateLettersAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/update_file_letters/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const updateTechsAPI = {
  async send(fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>('archive/update_file_techs/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const downloadAPI = {
  async download(filename: string): Promise<AxiosResponse<File>> {
    return instancePrivate.get<File>(`/archive/download_podd/${filename}`, {
      responseType: 'blob',
    });
  },
};

export const downloadReportsAPI = {
  async download(filename: string): Promise<AxiosResponse<File>> {
    return instancePrivate.get<File>(`/archive/download_reports/${filename}`, {
      responseType: 'blob',
    });
  },
};

export const downloadProjectsAPI = {
  async download(filename: string): Promise<AxiosResponse<File>> {
    return instancePrivate.get<File>(`/archive/download_projects/${filename}`, {
      responseType: 'blob',
    });
  },
};

export const downloadLettersAPI = {
  async download(filename: string): Promise<AxiosResponse<File>> {
    return instancePrivate.get<File>(`/archive/download_letters/${filename}`, {
      responseType: 'blob',
    });
  },
};

export const downloadTechsAPI = {
  async download(filename: string): Promise<AxiosResponse<File>> {
    return instancePrivate.get<File>(`/archive/download_techs/${filename}`, {
      responseType: 'blob',
    });
  },
};

//  API для получения данных раздела Эксплуатация
export const getUdsAPI = {
  async detail(): Promise<AxiosResponse<TUdsResponse>> {
    return instancePrivate.get<TUdsResponse>('/exploitation/uds', {});
  },
};

export const getUdsAllAPI = {
  async detail(): Promise<AxiosResponse<TUdsResponse>> {
    return instancePrivate.get<TUdsResponse>('/exploitation/uds_all', {});
  },
};

export const postUdsAPI = {
  async post_data(
    author: string,
    themes: string,
    executor: string,
    description: string,
    latitude: number,
    longitude: number,
    create_time: string,
    work_time: string,
    task_time: string,
    do_time: string,
    close_time: string,
  ): Promise<AxiosResponse<TUdss>> {
    return instancePrivate.post<TUdss>('/exploitation/uds', {
      author,
      themes,
      executor,
      description,
      latitude,
      longitude,
      create_time,
      work_time,
      task_time,
      do_time,
      close_time,
    });
  },
};

export const putUdsDescriptionAPI = {
  async put_description(
    id: number,
    author: string,
    themes: string,
    executor: string,
    description: string,
    latitude: number,
    longitude: number,
    create_time: string,
    work_time: string,
    task_time: string,
    do_time: string,
    close_time: string,
  ): Promise<AxiosResponse<TUdss>> {
    return instancePrivate.put<TUdss>(`/exploitation/uds_name/${id}`, {
      author,
      themes,
      executor,
      description,
      latitude,
      longitude,
      create_time,
      work_time,
      task_time,
      do_time,
      close_time,
    });
  },
};

export const putUdsWorkAPI = {
  async put_description(
    id: number,
    author: string,
    themes: string,
    executor: string,
    description: string,
    latitude: number,
    longitude: number,
    create_time: string,
    work_time: string,
    task_time: string,
    do_time: string,
    close_time: string,
  ): Promise<AxiosResponse<TUdss>> {
    return instancePrivate.put<TUdss>(`/exploitation/uds_work/${id}`, {
      author,
      themes,
      executor,
      description,
      latitude,
      longitude,
      create_time,
      work_time,
      task_time,
      do_time,
      close_time,
    });
  },
};

export const putUdsDoAPI = {
  async put_description(
    id: number,
    author: string,
    themes: string,
    executor: string,
    description: string,
    latitude: number,
    longitude: number,
    create_time: string,
    work_time: string,
    task_time: string,
    do_time: string,
    close_time: string,
  ): Promise<AxiosResponse<TUdss>> {
    return instancePrivate.put<TUdss>(`/exploitation/uds_do/${id}`, {
      author,
      themes,
      executor,
      description,
      latitude,
      longitude,
      create_time,
      work_time,
      task_time,
      do_time,
      close_time,
    });
  },
};

export const putUdsCloseAPI = {
  async put_description(
    id: number,
    author: string,
    themes: string,
    executor: string,
    description: string,
    latitude: number,
    longitude: number,
    create_time: string,
    work_time: string,
    task_time: string,
    do_time: string,
    close_time: string,
  ): Promise<AxiosResponse<TUdss>> {
    return instancePrivate.put<TUdss>(`/exploitation/uds_close/${id}`, {
      author,
      themes,
      executor,
      description,
      latitude,
      longitude,
      create_time,
      work_time,
      task_time,
      do_time,
      close_time,
    });
  },
};

export const getCommentAPI = {
  async detail(id: number): Promise<AxiosResponse<TCommentResponse>> {
    return instancePrivate.get<TCommentResponse>(`/exploitation/comment/${id}`, { params: { data_id: id } });
  },
};

export const postCommentAPI = {
  async post_data(id: number, author: string, description: string, times: string): Promise<AxiosResponse<TComments>> {
    return instancePrivate.post<TComments>(`/exploitation/comment/${id}`, {
      author,
      description,
      times,
    });
  },
};

export const udsFileAPI = {
  async send(id: number, fileUpload: File | null): Promise<AxiosResponse<File>> {
    const formData = new FormData();
    if (fileUpload) {
      formData.append('file', fileUpload);
    }
    return instancePrivate.post<File>(`/exploitation/uds_photo/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// export const photoAPI = {
//   async photo(id: number): Promise<AxiosResponse<Blob>> {
//     return instancePrivate.get<Blob>(`/exploitation/uds_photo/${id}`, {
//       responseType: 'blob',
//     });
//   },
// };

export const photoAPI = {
  async photo(id: number): Promise<AxiosResponse<string[]>> {
    return instancePrivate.get<string[]>(`/exploitation/uds_photo/${id}`, {});
  },
};
